import React from 'react';
import { osc, wobble, lerp, loop, clamp, easeOut } from '../../core/AnimationUtils';

/*
 * ConceptScenesMath2 — Anivaze v2 concept-acting scenes for early-maths ideas.
 * Two groups of apples slide together (addition) and a character eats cookies
 * one at a time (subtraction). Pure function of `t`; kid-friendly, bright,
 * rounded, no Date.now/Math.random. See design-plan/v2/ANIMATION-PATTERN.md.
 */

/* ── tiny polar helper ─────────────────────────────────────────────────────── */
const polar = (cx: number, cy: number, r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

/* ━━━ SHARED SUB-COMPONENTS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */

/** A round wicker basket centred at (cx, cy). */
const Basket = (cx: number, cy: number, accent: string) => (
  <g>
    {/* shadow */}
    <ellipse cx={cx} cy={cy + 44} rx={46} ry={8} fill="rgba(0,0,0,0.14)" />
    {/* body */}
    <path
      d={`M${cx - 46},${cy + 4} Q${cx - 46},${cy + 44} ${cx},${cy + 44} Q${cx + 46},${cy + 44} ${cx + 46},${cy + 4} Z`}
      fill="#f9a825" stroke="#e65100" strokeWidth={2.5}
    />
    {/* weave stripes */}
    {[-28, -10, 10, 28].map((dx) => (
      <line key={dx}
        x1={cx + dx} y1={cy + 6} x2={cx + dx + 4} y2={cy + 42}
        stroke="#e65100" strokeWidth={1.5} opacity={0.5}
      />
    ))}
    {/* rim */}
    <ellipse cx={cx} cy={cy + 4} rx={46} ry={9} fill={accent} stroke="#bf360c" strokeWidth={2} />
    {/* handle */}
    <path
      d={`M${cx - 30},${cy + 4} Q${cx - 32},${cy - 24} ${cx},${cy - 28} Q${cx + 32},${cy - 24} ${cx + 30},${cy + 4}`}
      fill="none" stroke="#bf360c" strokeWidth={3.5} strokeLinecap="round"
    />
  </g>
);

/** A shiny cartoon apple at (ax, ay). Phase shifts the wobble so each is unique. */
const Apple = (ax: number, ay: number, phase: number, t: number) => {
  const bob = wobble(t, 1.6, 2.5, phase);
  return (
    <g transform={`translate(${ax} ${ay + bob})`}>
      <ellipse cx={0} cy={15} rx={16} ry={4} fill="rgba(0,0,0,0.13)" />
      <circle cx={0} cy={0} r={16} fill="#e53935" stroke="#b71c1c" strokeWidth={1.5} />
      {/* shine */}
      <ellipse cx={-5} cy={-6} rx={5} ry={3.5} fill="rgba(255,255,255,0.45)" transform="rotate(-20 -5 -6)" />
      {/* stalk */}
      <rect x={-1.5} y={-20} width={3} height={8} rx={1.5} fill="#33691e" />
      {/* leaf */}
      <path d="M1,-17 Q10,-24 8,-14 Q4,-14 1,-17Z" fill="#558b2f" />
    </g>
  );
};

/* ━━━ ADDITION SCENE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface AdditionSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds elapsed since the scene started; pure-function of t. */
  t?: number;
}

/**
 * Two baskets of apples (2 on the left, 3 on the right) slide together into
 * one central basket every ~6 s. A bold number counts up (2 → 5) as the apples
 * land, and "2 + 3 = 5" reveals itself. A cheering stick-figure celebrates.
 */
export const AdditionScene: React.FC<AdditionSceneProps> = ({
  opacity = 1,
  accentColor = '#ef4444',
  label = 'Putting groups together = adding!',
  t = 0,
}) => {
  const CYCLE = 6;
  const c = loop(t, CYCLE);              // 0→1 seamless

  // Phase: 0–0.25 → baskets sit still; 0.25–0.65 → they slide in; 0.65–1 → celebrate
  const slideP = clamp((c - 0.25) / 0.4, 0, 1);
  const slidePEased = easeOut(slideP);

  // Left basket starts at x=130, right at x=670; both meet at centre x=400.
  const leftX = lerp(150, 370, slidePEased);
  const rightX = lerp(650, 430, slidePEased);

  // Running total visible once merging begins: at halfway both groups are ~nearby.
  // We reveal the count stepping up as the baskets approach.
  const countShown = slideP < 0.01
    ? 2
    : slideP < 0.45
      ? Math.min(2 + Math.floor(slideP * (3 / 0.45)), 4)
      : 5;

  // "+" and "=" labels draw on during the slide.
  const plusOpacity = clamp((c - 0.2) / 0.15, 0, 1);
  const equalsOpacity = clamp((slideP - 0.7) / 0.15, 0, 1);

  // Celebrate: cheering character arm angle
  const armAngle = c > 0.65 ? wobble(t, 0.5, 22, 0) : 0;
  const celebrateOpacity = clamp((c - 0.65) / 0.1, 0, 1);

  // "5" total number bounce
  const totalScale = c > 0.75
    ? 1 + osc(t, 0.7, 0, 0.22)
    : 1;

  // Stars that pop during celebration
  const starPositions = [
    { x: 200, y: 100, phase: 0 },
    { x: 600, y: 90, phase: 1.2 },
    { x: 120, y: 180, phase: 2.1 },
    { x: 680, y: 170, phase: 0.7 },
  ];

  return (
    <g opacity={opacity}>
      {/* Sky background */}
      <defs>
        <linearGradient id="add-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#e0f7fa" />
          <stop offset="100%" stopColor="#f9fbe7" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={800} height={450} fill="url(#add-sky)" />
      {/* Ground strip */}
      <rect x={0} y={360} width={800} height={90} rx={0} fill="#c8e6c9" />
      <rect x={0} y={360} width={800} height={8} fill="#a5d6a7" />

      {/* ── LEFT group label "2" before slide ─────────── */}
      <text
        x={leftX} y={155}
        textAnchor="middle" fill="#1b5e20"
        fontSize={28} fontWeight={800} fontFamily="Inter,sans-serif"
        opacity={clamp(1 - slideP * 3, 0, 1)}
      >2</text>

      {/* ── RIGHT group label "3" before slide ────────── */}
      <text
        x={rightX} y={155}
        textAnchor="middle" fill="#1b5e20"
        fontSize={28} fontWeight={800} fontFamily="Inter,sans-serif"
        opacity={clamp(1 - slideP * 3, 0, 1)}
      >3</text>

      {/* "+" sign between them */}
      <text
        x={400} y={195}
        textAnchor="middle" fill="#e53935"
        fontSize={42} fontWeight={900} fontFamily="Inter,sans-serif"
        opacity={plusOpacity * clamp(1 - (slideP - 0.5) * 4, 0, 1)}
      >+</text>

      {/* ── LEFT BASKET with 2 apples ──────────────────── */}
      <g transform={`translate(${leftX} 270)`}>
        {Basket(0, 0, accentColor)}
        {/* 2 apples */}
        {Apple(-18, -44, 0.3, t)}
        {Apple(18, -44, 1.7, t)}
      </g>

      {/* ── RIGHT BASKET with 3 apples ─────────────────── */}
      <g transform={`translate(${rightX} 270)`}>
        {Basket(0, 0, '#fb8c00')}
        {/* 3 apples */}
        {Apple(-22, -44, 0.9, t)}
        {Apple(0, -54, 2.4, t)}
        {Apple(22, -44, 1.2, t)}
      </g>

      {/* ── BIG RUNNING TOTAL ─────────────────────────── */}
      <g
        transform={`translate(400 90) scale(${totalScale})`}
        opacity={clamp(slideP * 4, 0, 1)}
      >
        <circle cx={0} cy={0} r={46} fill="#fff9c4" stroke="#f9a825" strokeWidth={3} />
        <text
          x={0} y={14}
          textAnchor="middle" fill="#e53935"
          fontSize={48} fontWeight={900} fontFamily="Inter,sans-serif"
        >{countShown}</text>
      </g>

      {/* "= 5" when merged */}
      <text
        x={400} y={148}
        textAnchor="middle" fill="#1b5e20"
        fontSize={22} fontWeight={700} fontFamily="Inter,sans-serif"
        opacity={equalsOpacity}
      >= {countShown} apples!</text>

      {/* ── CHEERING STICK FIGURE ─────────────────────── */}
      <g transform="translate(400 330)" opacity={celebrateOpacity}>
        {/* head */}
        <circle cx={0} cy={-60} r={18} fill="#fff9c4" stroke="#f9a825" strokeWidth={2.5} />
        {/* smile */}
        <path d="M-7,-55 Q0,-48 7,-55" fill="none" stroke="#e53935" strokeWidth={2} strokeLinecap="round" />
        {/* eyes */}
        <circle cx={-5} cy={-62} r={2.5} fill="#333" />
        <circle cx={5} cy={-62} r={2.5} fill="#333" />
        {/* body */}
        <line x1={0} y1={-42} x2={0} y2={-10} stroke="#333" strokeWidth={3.5} strokeLinecap="round" />
        {/* arms raised and waving */}
        <line
          x1={0} y1={-30}
          x2={-30 + wobble(t, 0.45, 6, 0)} y2={-52 + armAngle * 0.3}
          stroke="#333" strokeWidth={3} strokeLinecap="round"
        />
        <line
          x1={0} y1={-30}
          x2={30 - wobble(t, 0.45, 6, Math.PI)} y2={-52 + armAngle * 0.3}
          stroke="#333" strokeWidth={3} strokeLinecap="round"
        />
        {/* legs */}
        <line x1={0} y1={-10} x2={-16} y2={14} stroke="#333" strokeWidth={3} strokeLinecap="round" />
        <line x1={0} y1={-10} x2={16} y2={14} stroke="#333" strokeWidth={3} strokeLinecap="round" />
      </g>

      {/* ── CELEBRATION STARS ─────────────────────────── */}
      {starPositions.map((s, i) => {
        const starOpacity = clamp((c - 0.68) / 0.1, 0, 1) * clamp(1 - (c - 0.9) / 0.08, 0, 1);
        const scale = 0.7 + osc(t, 0.4 + i * 0.07, 0, 0.6, s.phase);
        // Build 5-point star polygon string: alternating outer(r=16) and inner(r=7) points
        const starPts = Array.from({ length: 10 }, (_, k) => {
          const r = k % 2 === 0 ? 16 : 7;
          const ang = -90 + k * 36;          // 36° between successive points
          const [px, py] = polar(0, 0, r, ang);
          return `${px},${py}`;
        }).join(' ');
        return (
          <g key={i} transform={`translate(${s.x} ${s.y}) scale(${scale})`} opacity={starOpacity}>
            <polygon points={starPts} fill="#ffd54f" stroke="#f9a825" strokeWidth={1} />
          </g>
        );
      })}

      {/* Caption */}
      <g transform="translate(400 422)">
        <rect x={-270} y={-20} width={540} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#f9a825" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle" fill="#1b5e20" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};

/* ━━━ SUBTRACTION SCENE ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */
export interface SubtractionSceneProps {
  opacity?: number;
  accentColor?: string;
  label?: string;
  /** Seconds elapsed since the scene started; pure-function of t. */
  t?: number;
}

/** A round cartoon cookie at (cx, cy). */
const Cookie = (cx: number, cy: number, phase: number, t: number, flyProgress: number) => {
  // flyProgress 0 = sitting in basket, 1 = fully floated away & eaten
  const flyX = lerp(0, 90 + phase * 20, easeOut(flyProgress));
  const flyY = lerp(0, -130 - phase * 30, easeOut(flyProgress));
  const rot = flyProgress * (40 + phase * 30);
  const vis = clamp(1 - flyProgress * 2.5, 0, 1);
  const bob = flyProgress < 0.05 ? wobble(t, 1.4, 2.5, phase) : 0;
  return (
    <g
      transform={`translate(${cx + flyX} ${cy + flyY + bob}) rotate(${rot})`}
      opacity={vis}
    >
      <ellipse cx={0} cy={12} rx={17} ry={4} fill="rgba(0,0,0,0.12)" opacity={1 - flyProgress} />
      <circle cx={0} cy={0} r={17} fill="#d4813e" stroke="#8d4a1d" strokeWidth={1.8} />
      {/* chocolate chips derived from phase, no Math.random */}
      {[
        { x: -6, y: -5, r: 3.2 },
        { x: 5, y: -7, r: 2.8 },
        { x: -8, y: 5, r: 2.4 },
        { x: 7, y: 5, r: 3 },
        { x: 0, y: 2, r: 2.2 },
      ].map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={c.r} fill="#3e1f00" />
      ))}
    </g>
  );
};

/**
 * A basket starts with 5 cookies; every ~1 s the cheeky character grabs one —
 * it floats up and vanishes with a "nom" pop. The bold counter ticks down
 * (5 → 4 → 3 → 2 → 1 → 0), and "5 − ? = ?" appears. Loops every ~6 s.
 */
export const SubtractionScene: React.FC<SubtractionSceneProps> = ({
  opacity = 1,
  accentColor = '#f59e0b',
  label = 'Taking away = subtracting!',
  t = 0,
}) => {
  const CYCLE = 6.5;
  const c = loop(t, CYCLE);              // 0→1 seamless loop phase

  // 5 cookies taken one per ~0.9 s; taking begins at c=0.05
  const TAKE_START = 0.05;
  const TAKE_GAP = 0.15;                 // fraction of cycle between each take

  // How many have been fully taken (flown away) so far in this loop
  const cookiesTaken = Math.min(5, Math.max(0, Math.floor((c - TAKE_START) / TAKE_GAP)));
  const remaining = 5 - cookiesTaken;

  // For the currently-flying cookie, its in-flight progress (0→1 over TAKE_GAP/2)
  const currentFlyPhase = cookiesTaken < 5
    ? clamp(((c - TAKE_START) % TAKE_GAP) / (TAKE_GAP * 0.5), 0, 1)
    : 1;

  // Character stretch/reach when grabbing
  const reachAmp = currentFlyPhase < 0.3 ? currentFlyPhase / 0.3 : Math.max(0, 1 - (currentFlyPhase - 0.3) / 0.4);

  // "Nom!" pop opacity — appears briefly when a cookie flies off
  const nomOpacity = clamp(
    currentFlyPhase > 0.1 && currentFlyPhase < 0.55
      ? Math.sin((currentFlyPhase - 0.1) / 0.45 * Math.PI)
      : 0,
    0, 1,
  );

  // Counter number scale bounce on each decrement
  const counterScale = 1 + clamp(
    (1 - currentFlyPhase * 3) * 0.3,
    0, 0.3,
  ) * (cookiesTaken > 0 ? 1 : 0);

  // "minus" symbol opacity
  const minusOpacity = clamp((c - 0.03) / 0.08, 0, 1);

  // Basket sits in the lower-centre
  const basketX = 300;
  const basketY = 260;

  // Cookie arrangement in basket (fixed offsets, no random)
  const cookieSlots = [
    { dx: -34, dy: -12, phase: 0.3 },
    { dx: 0, dy: -20, phase: 1.1 },
    { dx: 34, dy: -12, phase: 1.9 },
    { dx: -17, dy: 8, phase: 0.7 },
    { dx: 17, dy: 8, phase: 2.4 },
  ];

  return (
    <g opacity={opacity}>
      <defs>
        <linearGradient id="sub-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fce4ec" />
          <stop offset="100%" stopColor="#fff9c4" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={800} height={450} fill="url(#sub-sky)" />
      {/* Ground */}
      <rect x={0} y={360} width={800} height={90} fill="#ffe0b2" />
      <rect x={0} y={360} width={800} height={8} fill="#ffcc80" />

      {/* ── BASKET ─────────────────────────────────────── */}
      <g transform={`translate(${basketX} ${basketY})`}>
        {Basket(0, 0, accentColor)}

        {/* Render each cookie slot; already-taken ones are gone, current one is mid-flight */}
        {cookieSlots.map((slot, i) => {
          if (i < cookiesTaken - 1) return null;                // already eaten
          const flyP = i === cookiesTaken - 1
            ? 1                                                   // fully gone
            : i === cookiesTaken
              ? currentFlyPhase                                   // currently flying
              : 0;                                                // still sitting
          if (flyP >= 1) return null;
          return (
            <g key={i}>
              {Cookie(slot.dx, slot.dy - 30, slot.phase, t, flyP)}
            </g>
          );
        })}
      </g>

      {/* ── CHARACTER on the right ──────────────────────── */}
      <g transform="translate(570 310)">
        {/* body */}
        <ellipse cx={0} cy={20} rx={26} ry={30} fill="#fff9c4" stroke="#f9a825" strokeWidth={2.5} />
        {/* head */}
        <circle cx={0} cy={-30} r={24} fill="#fff9c4" stroke="#f9a825" strokeWidth={2.5} />
        {/* eyes — widen when grabbing */}
        <circle cx={-8} cy={-33} r={3.5 + reachAmp * 1.5} fill="#333" />
        <circle cx={8} cy={-33} r={3.5 + reachAmp * 1.5} fill="#333" />
        {/* shine in eyes */}
        <circle cx={-6} cy={-35} r={1.2} fill="white" />
        <circle cx={10} cy={-35} r={1.2} fill="white" />
        {/* big grin */}
        <path
          d={`M-10,-22 Q${reachAmp * 2},-14 10,-22`}
          fill="none" stroke="#e53935" strokeWidth={2.5} strokeLinecap="round"
        />
        {/* reaching arm, extends left toward basket */}
        <line
          x1={-20} y1={10}
          x2={-58 - reachAmp * 44} y2={-8 + reachAmp * 12}
          stroke="#f9a825" strokeWidth={6} strokeLinecap="round"
        />
        <circle
          cx={-58 - reachAmp * 44} cy={-8 + reachAmp * 12}
          r={7} fill="#fff9c4" stroke="#f9a825" strokeWidth={2}
        />
        {/* idle arm */}
        <line x1={20} y1={10} x2={40} y2={30} stroke="#f9a825" strokeWidth={6} strokeLinecap="round" />
        {/* legs */}
        <line x1={-10} y1={48} x2={-18} y2={72} stroke="#f9a825" strokeWidth={5} strokeLinecap="round" />
        <line x1={10} y1={48} x2={18} y2={72} stroke="#f9a825" strokeWidth={5} strokeLinecap="round" />
      </g>

      {/* ── "NOM!" pop ──────────────────────────────────── */}
      <g transform="translate(510 170)" opacity={nomOpacity}>
        <rect x={-32} y={-18} width={64} height={36} rx={10} fill="#e53935" />
        <text x={0} y={7} textAnchor="middle" fill="white" fontSize={18} fontWeight={900} fontFamily="Inter,sans-serif">NOM!</text>
      </g>

      {/* ── BIG COUNTER ─────────────────────────────────── */}
      <g transform={`translate(650 160) scale(${counterScale})`}>
        <circle cx={0} cy={0} r={54} fill="#fff9c4" stroke="#f9a825" strokeWidth={3.5} />
        <text
          x={0} y={18}
          textAnchor="middle" fill="#e53935"
          fontSize={58} fontWeight={900} fontFamily="Inter,sans-serif"
        >{remaining}</text>
      </g>
      <text
        x={650} y={228}
        textAnchor="middle" fill="#b71c1c"
        fontSize={14} fontWeight={700} fontFamily="Inter,sans-serif"
      >cookies left</text>

      {/* ── Subtraction expression ──────────────────────── */}
      <g opacity={minusOpacity}>
        <text
          x={650} y={278}
          textAnchor="middle" fill="#5d4037"
          fontSize={18} fontWeight={700} fontFamily="Inter,sans-serif"
        >5 − {cookiesTaken} = {remaining}</text>
      </g>

      {/* ── "−" badge floats beside basket ──────────────── */}
      <g transform={`translate(180 ${basketY - 30})`} opacity={minusOpacity}>
        <circle cx={0} cy={0} r={24} fill="#e53935" />
        <text x={0} y={8} textAnchor="middle" fill="white" fontSize={30} fontWeight={900} fontFamily="Inter,sans-serif">−</text>
      </g>

      {/* Caption */}
      <g transform="translate(400 422)">
        <rect x={-270} y={-20} width={540} height={36} rx={18} fill="rgba(255,255,255,0.88)" stroke="#ffcc80" strokeWidth={2} />
        <text x={0} y={5} textAnchor="middle" fill="#4e342e" fontSize={15} fontWeight={700} fontFamily="Inter,sans-serif">
          {label}
        </text>
      </g>
    </g>
  );
};
